import ProfessionalRepository from '@/repositories/ProfessionalRepository'
import { IModule } from '@/models/IModule'
import { IAssetModule } from '@/models/IAssetModule'
import AssetRepository from '@/repositories/AssetRepository'
import { IAssetCompleteInformation } from '@/models/IAssetCompleteInformation'
import { IAssetLiquid } from '@/models/IAssetLiquid'
import AssetLiquidRepository from '@/repositories/AssetLiquidRepository'
import { nextTick } from 'vue'

const state = () => ({
    asset_modules: [],
    assets: [],
    liquids: [],
    refresh: false,
    first_suiclean_id_domicile: null,
})

const mutations = {
    setAssetModules(state, asset_modules: IAssetModule[]): void {
        state.asset_modules = asset_modules
    },

    setAssetsByAssetModule(state, payload: { id_module: number; assets: IAssetCompleteInformation[] }): void {
        state.assets[payload.id_module] = payload.assets
    },

    setAssetByAssetModule(state, payload: { id_module: number; asset: IAssetCompleteInformation }): void {
        const index = state.assets[payload.id_module].findIndex((item) => item.id_asset === payload.asset.id_asset)
        if (index !== -1) {
            state.assets[payload.id_module][index] = payload.asset
        }
        state.refresh = true
        nextTick(() => {
            state.refresh = false
        })
    },

    refreshAssetPage(state): void {
        state.refresh = true
        nextTick(() => {
            state.refresh = false
        })
    },

    setLiquidsByAssetModule(state, payload: { id_module: number; liquids: IAssetLiquid[] }): void {
        state.liquids[payload.id_module] = payload.liquids
    },

    setFirstSuicleanIdDomicile(state, id_domicile: number | null): void {
        state.first_suiclean_id_domicile = id_domicile
    },

    cleanAssetModuleStore(state): void {
        state.asset_modules = []
        state.assets = []
        state.liquids = []
        state.refresh = false
        state.first_suiclean_id_domicile = null
    },
}

const actions = {
    cleanAssetModuleStore({ commit }) {
        commit('cleanAssetModuleStore')
    },

    setFirstSuicleanIdDomicile({ commit }, id_domicile: number | null) {
        commit('setFirstSuicleanIdDomicile', id_domicile)
    },

    async fetchAssetModules({ commit }): Promise<void> {
        return ProfessionalRepository.getProfessionalModulesV2().then((success) => {
            const modules: Array<IModule> = success.data.data.filter((module: IModule) => module.is_generic)
            modules.forEach((module, index) => {
                AssetRepository.getAssetsCountByIdModuleAndIdClient(module.id_module).then((success) => {
                    if (success.data.data.count === 0) {
                        modules.splice(
                            modules.findIndex((item) => item === module),
                            1
                        )
                    }
                })
            })

            commit('setAssetModules', modules)
        })
    },

    async fetchAssetsOfAssetModule({ commit }, id_module: number): Promise<void> {
        return AssetRepository.getAssetsByIdModuleAndIdClient(id_module).then((success) => {
            commit('setAssetsByAssetModule', { id_module: id_module, assets: success.data.data.data })
        })
    },

    async fetchAssetLiquidByIdModule({ commit }, payload: { id_pro: number; id_module: number }): Promise<void> {
        return AssetLiquidRepository.getAssetLiquidByIdModule(payload.id_pro, payload.id_module).then((success) => {
            commit('setLiquidsByAssetModule', { id_module: payload.id_module, liquids: success.data.data.sort((a, b) => a.name.localeCompare(b.name)) })
        })
    },

    async handleAssetSoftUpdate({ commit }, payload: { id_asset: number; id_module: number }): Promise<void> {
        return AssetRepository.getAssetById(payload.id_asset).then((success) => {
            commit('setAssetByAssetModule', { id_module: payload.id_module, asset: success.data.data })
        })
    },
}

const getters = {
    asset_modules: (state) => state.asset_modules,

    has_suiclean: (state) => state.asset_modules.some((asset_module) => asset_module.designation === 'GNC_SCLN'),

    suiclean_module: (state) => state.asset_modules.find((asset_module) => asset_module.designation === 'GNC_SCLN'),

    first_suiclean_id_domicile: (state) => state.first_suiclean_id_domicile,

    assets_by_asset_module: (state) => (id_module) => {
        return state.assets[id_module]
    },

    assets_by_asset_module_and_id_asset: (state) => (id_module, id_asset) => {
        return state.assets[id_module].find((asset) => asset.id_asset === parseInt(id_asset))
    },

    asset_model_by_id: (state) => (id_module: number, id_asset_model: number) => {
        return state.asset_modules
            .find((asset_module) => asset_module.id_module === id_module, 1)
            .asset_models.find((asset_model) => asset_model.id_asset_model === id_asset_model, 1)!
    },

    liquid_by_id_module_and_id_asset_and_model: (state) => (id_module: number, id_asset: number, index_model: number) => {
        const configuration = state.assets[id_module].find((asset) => asset.id_asset === id_asset, 1).configuration

        return state.liquids[id_module].find((liquid) => liquid.id_asset_liquid === configuration[index_model].id_asset_liquid, 1)!
    },

    liquids_by_asset_module: (state) => (id_module: number) => {
        return state.liquids[id_module]
    },

    refresh: (state) => state.refresh,

    getDevicesContractPaidByPro: (state) => {
        let result = false
        Object.keys(state.assets).forEach((key) => {
            state.assets[key]?.forEach((asset) => {
                asset.devices?.forEach((device) => {
                    if (device.contract_paid_by_professional) {
                        result = true
                    }
                })
            })
        })
        return result
    },
}

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
}
